/**
 * The venue card (И2 detail, contract #7 + #8 + #9/#10 + #16/#17 + the additive
 * #22.5 link writes) — tasks T.C4a and T.F2.
 *
 * Layout: header (identity + the ADMIN-writable curator fields), a stat strip,
 * then the sections the detail fan-out fills — quotas per program × format, the
 * link registry, the comment thread, the alias dictionary. The applications
 * drill-in and the unmatched-address queue are links into `/applications`
 * (T.C3 reads `venueId` / `hasVenue` there).
 *
 * Everything is read-only unless the session's role says otherwise: `#8`, the
 * alias writes and the #22.5 link writes are ADMIN, `#16` is MANAGER+. The
 * gates mirror the api's own `@Roles` — the server stays the authority, the UI
 * only avoids a request it knows will be refused.
 *
 * T.F2 made the card editable in three places, all optimistic with rollback and
 * all audited server-side (the hint under each form says so):
 *   * the manager is picked from `GET /users/directory` (25.5) with the T.R4
 *     Combobox — a manager is a real `AppUser`, not a bare id to type;
 *   * `normalizedAddress` is editable inline (#8) — it is the matcher's key, so
 *     the field says the value is stored normalized;
 *   * the link registry rows edit / add / delete (#22.5).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../api/client';
import { useOnlinePivot } from '../../api/online-pivot';
import type { TeacherDto } from '../../api/teachers';
import { useTeachers } from '../../api/teachers';
import type { VenueCourseFormat, VenueDetailDto, VenueKind, VenuePatch } from '../../api/venues';
import { usePatchVenue, useVenue, venueDetailQueryKey } from '../../api/venues';
import { useCreateUser } from '../../api/admin';
import {
  fetchUserDirectory,
  useCreateVenueLink,
  useDeleteVenueLink,
  usePatchVenueLink,
  useUserDirectory,
  userDirectoryQueryKey,
} from '../../api/venue-links';
import type { UserDirectoryEntry } from '../../api/venue-links';
import { Button, Drawer, EmptyState, ErrorState, Field, Input, Select, Spinner, Badge } from '../../components/ui';
import {
  CURATOR_CREATE_BUTTON,
  CURATOR_CREATE_FAILED_TEXT,
  CURATOR_CREATED_TEXT,
  CURATOR_LEGACY_HINT,
  CURATOR_LINK_FAILED_TEXT,
  CURATOR_MANAGER_NOTE,
  CURATOR_NAME_REQUIRED,
  CURATOR_OLD_LINK_HINT,
  CURATOR_PROMPT_HINT,
  CURATOR_PROMPT_TITLE,
  CURATOR_REMINDER_HINT,
  CURATOR_REMINDER_TITLE,
  CURATOR_SKIP_BUTTON,
  CURATOR_USERS_CARD_HINT,
  CURATOR_USERS_EMPTY,
  CURATOR_USERS_HINT,
  CURATOR_USERS_TITLE,
  curatorAccountCreateFrom,
  curatorAccountState,
  curatorCreateOutcome,
  curatorPaneAfterCreate,
  curatorPromptNeeded,
  curatorUserIdsWith,
  curatorUsersOf,
  findCuratorAccount,
  isCuratorEmailTaken,
  type CuratorCreateAttempt,
} from './curator-account-model';
import { parsePlanInput } from '../quotas/quotas-model';
import { Combobox } from '../../components/Combobox';
import { TableWrap } from '../../components/TableWrap';

import { VenueAliases } from './VenueAliases';
import { VenueComments } from './VenueComments';
import { VenueStatusBadges } from './VenueStatusBadges';
import './venues.css';
import {
  allowCuratorChildrenPatch,
  applicationsDrillDown,
  applicationsTotal,
  controlStatusLabel,
  curatorLabel,
  describeFailure,
  formatLabel,
  isPartnerVenue,
  offlineTrendBars,
  partnerLabel,
  partnerPoolOf,
  programsSelectionLines,
  quotaSheetDraftFrom,
  quotaSheetPatchFrom,
  quotaSheetRows,
  sheetCellLabel,
  TEACHERS_PREVIEW_LIMIT,
  teachersRosterPath,
  venueDepartmentLabel,
  venueDepartmentPatch,
  venuePrefillPath,
  venueKindLabel,
  venueManagerIds,
  venuePatchFrom,
  managerUserIdsPatchFrom,
  venuesListPath,
  type QuotaSheetDraft,
  type VenuesListParams,
  OFFLINE_TREND_EMPTY_TITLE,
  OFFLINE_TREND_HINT,
  OFFLINE_TREND_TITLE,
  PROGRAMS_SELECTION_EMPTY_TITLE,
  PROGRAMS_SELECTION_HINT,
} from './venues-model';
import {
  AUDIT_HINT,
  EMPTY_LINK_FORM,
  linkCreateFrom,
  linkErrorText,
  linkFieldError,
  linkFormFrom,
  linkFormIsDirty,
  linkPatchFrom,
  managerComboboxOptions,
  managerDisplayLabel,
} from './venue-links-model';
import { MISSING, formatCount } from '../dashboard/format';
import { venueDepartmentSelectOptions } from '../../departments';
import { teacherCountLabel } from '../teachers/teachers-model';
import { venuePrefillAccess } from '../cabinet/cabinet-model';
import { useAuth } from '../../auth/AuthContext';
import { hasAtLeast } from '../../auth/roles';

const LINK_FORMAT_OPTIONS: ReadonlyArray<{ value: VenueCourseFormat | ''; label: string }> = [
  { value: '', label: 'Не указан' },
  { value: 'OFFLINE', label: 'Офлайн' },
  { value: 'ONLINE', label: 'Онлайн' },
];

export function VenueCard({ venueId, listParams }: { venueId: number; listParams: VenuesListParams }) {
  const { role, cabinetVenueIds } = useAuth();
  const query = useVenue(venueId);
  const edit = usePatchVenue(venueId);

  // Wave 6 (owner): every edit lives in the right pane, the card itself is for
  // reading. The pane is a disclosure, not a destination — so it stays component
  // state, the way the «Онлайн-обучение» block reasons about its own toggle.
  const [editOpen, setEditOpen] = useState(false);
  const closeEdit = () => {
    edit.reset();
    setEditOpen(false);
  };

  // The api gates these; the UI only avoids a doomed request.
  const canAdmin = hasAtLeast(role, 'ADMIN');
  const canPost = hasAtLeast(role, 'MANAGER');

  if (query.isPending) {
    return (
      <div className="card" data-testid="venue-card-skeleton">
        <Spinner label="Загружаем карточку площадки" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="card" data-testid="venue-card-error">
        <ErrorState
          message={describeFailure(query.error as ApiError | null, 'Не удалось загрузить карточку площадки.')}
          onRetry={() => void query.refetch()}
        />
        <p className="venue-head__back">
          <Link to={venuesListPath(listParams)} data-testid="venue-back">
            ← К списку площадок
          </Link>
        </p>
      </div>
    );
  }

  const venue = query.data;
  const backTo = venuesListPath(listParams);

  return (
    <article data-testid="venue-card">
      <div className="card venue-section">
        <div className="venue-section__body">
          <VenueHeader venue={venue} backTo={backTo} canAdmin={canAdmin} onEdit={() => setEditOpen(true)} />
        </div>
      </div>

      {canAdmin && editOpen ? (
        <VenueEditPane venue={venue} edit={edit} onClose={closeEdit} />
      ) : null}

      <section className="venue-stats" aria-label="Показатели площадки">
        <StatBlock
          testId="venue-stat-applications"
          num={
            <Link to={applicationsDrillDown(venueId, listParams.region)} data-testid="applications-drill">
              {formatCount(applicationsTotal(venue.appStatusCounts))}
            </Link>
          }
          label="Заявления"
        />
        {/* T.V1 — the plan is the venue's own two numbers now; the legacy quota rows are
            an empty table, so the strip reads the venue like everything else does. */}
        <StatBlock
          testId="venue-stat-plan"
          num={formatCount(venue.planOffline + venue.planOnline)}
          label="План по квотам"
        />
        <StatBlock testId="venue-stat-links" num={formatCount(venue.links.length)} label="Ссылки" />
        {/* T.R10 — the count is the way into the venue-filtered teacher roster. */}
        <StatBlock
          testId="venue-stat-teachers"
          num={
            <Link to={teachersRosterPath(venue.id)} data-testid="teachers-drill">
              {formatCount(venue.teachersSummary?.total)}
            </Link>
          }
          label="Преподаватели"
        />
        <StatBlock testId="venue-stat-comments" num={formatCount(venue.commentCount)} label="Комментарии" />
        {/* T.Z26 (owner): the sixth block — «Заявления без площадки (не сопоставлены)» — is
            GONE. #7 answers a GLOBAL count there (`venueId: null`, the whole manual repair
            backlog), so every venue card printed the same 1 265 and read as this venue's
            own failure; the number's honest home is the dashboard's «Без площадки» card and
            the `/applications?hasVenue=false` list it opens. The contract field stays. */}
      </section>

      {/* T.R10 — the venue's people, listed where the manager is looking: the first few ФИО
          of the #7.5 roster scoped to this venue, the rest one click away. */}
      <VenueTeachers venueId={venue.id} />

      {/* T.V2 — the plan is the venue's OWN two numbers (T.V1), so the section is two
          stat-like blocks with the fact beside each, not a per-programme table. */}
      <VenuePlans venue={venue} canAdmin={canAdmin} />

      {/* T.Z27 — the online set this venue's manager keeps, the cabinet's own working
          table one click away, only where the api would actually serve it. */}
      <VenuePrefillSection venue={venue} role={role} cabinetVenueIds={cabinetVenueIds} />

      {/* Wave 9 (T.Y9) — the partner's SHARED pool, read-only here (D1/A14): the one
          number every branch of the partner reports as its plan, edited through #29
          in Settings so the audit trail of the pool has one address. */}
      {isPartnerVenue(venue) ? <PartnerPoolBlock venue={venue} /> : null}

      <ProgramsSelection venue={venue} />

      <OfflineTrend venue={venue} />

      <VenueLinks venue={venue} canEdit={canAdmin} />

      <QuotaSheetSection venue={venue} canAdmin={canAdmin} onEdit={() => setEditOpen(true)} />

      <VenueComments venueId={venueId} commentCount={venue.commentCount} canPost={canPost} />

      {/* `key={venue.id}` — T.Z35c: the «Адреса» section's «Обновить связи» offer is a
          PANE-SESSION state (`MatchRunPhase`), so the section must remount when the pane
          opens another venue instead of carrying the previous venue's offer onto it. */}
      <VenueAliases
        key={venue.id}
        venueId={venueId}
        venueName={venue.name}
        venueAddress={venue.normalizedAddress}
        canEdit={canAdmin}
      />
    </article>
  );
}

// ---------------------------------------------------------------------------
// Header + inline admin edit (#8 + the 25.5 manager picker)
// ---------------------------------------------------------------------------

function VenueHeader({
  venue,
  backTo,
  canAdmin,
  onEdit,
}: {
  venue: VenueDetailDto;
  backTo: string;
  canAdmin: boolean;
  onEdit: () => void;
}) {
  // One directory read answers both «who is the manager» and «who are the venue's
  // linked curators» (25.5 is VIEWER-readable, so every role's card has the names).
  const directory = useUserDirectory();
  const managerName = managerDisplayLabel(venue.managerUserId, directory);
  const curatorUsers = curatorUsersOf(venue.curatorUserIds, directory);

  return (
    <header className="venue-head">
      <div className="venue-head__main">
        <p className="venue-head__back">
          <Link to={backTo} data-testid="venue-back">
            ← К списку площадок
          </Link>
        </p>
        <h1 className="venue-head__title" data-testid="venue-card-name">
          {venue.name}
        </h1>
        {/* Wave 9 (T.Y9) — the reporting kind, on the card like on the roster row:
            a partner filial's plan is the partner's SHARED pool, and the badge is
            what says so before anyone reads «План онлайн» as the venue's own. */}
        <span className="venue-kind" data-testid="venue-card-kind">
          <Badge tone={isPartnerVenue(venue) ? 'warn' : 'neutral'} title={venueKindTitle(venue.kind, venue.partnerName)}>
            {venueKindLabel(venue.kind)}
          </Badge>
          {isPartnerVenue(venue) ? (
            <span className="venue-kind__partner" data-testid="venue-card-partner">
              {partnerLabel(venue)}
            </span>
          ) : null}
        </span>
        <dl className="venue-head__meta">
          <div>
            <dt>Регион</dt>
            <dd data-testid="venue-card-region">{venue.region ?? MISSING}</dd>
          </div>
          <div>
            <dt>Адрес</dt>
            <dd data-testid="venue-card-address">{venue.normalizedAddress ?? MISSING}</dd>
          </div>
          <div>
            <dt>Менеджер</dt>
            <dd data-testid="venue-card-manager">{managerName}</dd>
          </div>
          {/* T.Z94 (v1.9.15) — the venue's OWN department, the field the admin sets on
              the card. «Не задано» is not «без отдела»: with nothing here the department
              is still DERIVED from the managers' (the T.Z56 join), and the label must
              not claim a state the data does not hold. The same field the edit form's
              select writes; when set it wins over the managers everywhere. */}
          <div>
            <dt>Отдел</dt>
            <dd data-testid="venue-card-department">{venueDepartmentLabel(venue)}</dd>
          </div>
          <div>
            <dt>Контроль</dt>
            <dd data-testid="venue-card-control">{controlStatusLabel(venue.controlStatus)}</dd>
          </div>
          {/* T.R8 — one venue attribute, editable through #8 like the rest of the
              header. A8 (T.Z12): the hint keeps the sheet column from reading as an
              access grant — the row below is the one that says who can sign in. */}
          <div>
            <dt>Куратор</dt>
            <dd data-testid="venue-card-curator" title={CURATOR_LEGACY_HINT}>
              {curatorLabel(venue.curatorName)}
              {venue.curatorPhone ? ` · ${venue.curatorPhone}` : ''}
              {venue.curatorEmail ? ` · ${venue.curatorEmail}` : ''}
              <span className="venue-head__meta-hint" data-testid="venue-card-curator-legacy-hint">
                {CURATOR_LEGACY_HINT}
              </span>
            </dd>
          </div>
          {/* A8 (T.Z12) — the LINKED curator accounts (`venue_curators`, the same set
              the drawer's D20 prompt grows): the ФИО/телефон/почта above is sheet text
              with no account behind it, THESE are the people who can actually log in.
              Resolved against the 25.5 directory, the api's order kept. */}
          <div>
            <dt>{CURATOR_USERS_TITLE}</dt>
            <dd data-testid="venue-card-curator-users">
              {curatorUsers.length === 0 ? (
                <span className="cell-muted">{CURATOR_USERS_EMPTY}</span>
              ) : (
                <>
                  {curatorUsers.map((user) => (
                    <span
                      className="venue-head__curator"
                      key={user.id}
                      data-testid="venue-card-curator-user"
                      data-user-id={user.id}
                      title={user.email ?? undefined}
                    >
                      <span className="venue-head__curator-name">{user.name}</span>
                      {user.email ? <span className="venue-head__curator-email">{user.email}</span> : null}
                    </span>
                  ))}
                  <span className="venue-head__meta-hint" data-testid="venue-card-curator-users-hint">
                    {CURATOR_USERS_CARD_HINT}
                  </span>
                </>
              )}
            </dd>
          </div>
        </dl>
        <div className="venue-badges" data-testid="venue-card-statuses">
          <VenueStatusBadges counts={venue.appStatusCounts} testIdPrefix="venue-status" />
        </div>
      </div>

      <div className="venue-head__actions">
        {canAdmin ? (
          <>
            {/* The pane is the only editor now: the header's fields were edited
                in place until wave 6, and a manager looking for «где менять» had
                to find three separate forms on one card. */}
            <Button size="sm" data-testid="venue-edit-toggle" onClick={onEdit}>
              Редактировать
            </Button>
            <p className="venue-head__audit-hint" data-testid="venue-audit-hint">
              {AUDIT_HINT}
            </p>
          </>
        ) : null}
      </div>
    </header>
  );
}

/**
 * The right pane (wave 6, owner): EVERY edit of the card lives here, the card
 * itself stays for reading. The pattern is the teachers row drawer (T.R7) —
 * `<Drawer>` over an overlay, focus trapped, `Escape` closes — and the fields are
 * the ones the header used to edit in place plus the whole «Квоты-лист» block the
 * manager was correcting in a private copy of the sheet.
 *
 * One form, one PATCH (#8), one audit row: the identity block and the sheet block
 * are the same table's columns, and a save that wrote them separately would make
 * «what changed» a matter of which button was pressed last. Only the keys that
 * differ from the venue travel, so a save that changes nothing is refused before
 * it is sent (an empty body is a 400 by contract).
 *
 * «Регион» is deliberately read-only here: it is a value of the shared region
 * dictionary (region_norm, D2) folded at import, and #8 does not accept it — the
 * pane shows it so the block still reads as the venue's address block, not as a
 * claim that it is editable.
 */
function VenueEditPane({
  venue,
  edit,
  onClose,
}: {
  venue: VenueDetailDto;
  edit: ReturnType<typeof usePatchVenue>;
  onClose: () => void;
}) {
  // Names for the manager picker. The directory is VIEWER-visible (25.5) and
  // degrades to the bare id when it cannot be read (an older api).
  const directory = useUserDirectory();

  // A8 (T.Z12) — the venue's linked curator accounts, resolved against the same
  // read. The block is READ-ONLY on purpose: the set has one writer (#8
  // `curatorUserIds`, a REPLACE-SET) and the D20 prompt below is the one flow that
  // grows it from this drawer — a save that creates the account lands the link, and
  // the refetch brings the new name here.
  const curatorUsers = curatorUsersOf(venue.curatorUserIds, directory);

  // Z3 — the manager editor stages the WHOLE set (`managerUserIds`, a REPLACE-SET),
  // not the deprecated first-manager scalar: the scalar folds into a one-element
  // set on the api side, so a venue with several managers lost every one but the
  // first on save — and a non-first manager could never be removed at all.
  const [managerIds, setManagerIds] = useState<number[]>(() => venueManagerIds(venue));
  const [controlStatus, setControlStatus] = useState(venue.controlStatus ?? '');
  const [address, setAddress] = useState(venue.normalizedAddress ?? '');
  const [curatorName, setCuratorName] = useState(venue.curatorName ?? '');
  const [curatorPhone, setCuratorPhone] = useState(venue.curatorPhone ?? '');
  const [curatorEmail, setCuratorEmail] = useState(venue.curatorEmail ?? '');
  // Wave 9 (T.Y9, D3) — «разрешить куратору вносить детей (онлайн-набор)». The flag
  // decides whether this venue's curator sees the online prefill surface at all.
  const [allowCuratorChildren, setAllowCuratorChildren] = useState(venue.allowCuratorChildren === true);
  // T.Z94 (v1.9.15) — the venue's OWN department, staged as the select's wire text
  // ('' = «не задано» → the api stores `null`). When it lands, the T.Z56 derivation
  // answers THIS value over the managers' fold in every «Отдел» consumer.
  const [department, setDepartment] = useState(venue.department ?? '');
  const [sheet, setSheet] = useState<QuotaSheetDraft>(() => quotaSheetDraftFrom(venue));

  // The draft re-seeds only when the api has actually stored something new: the
  // row's `updatedAt` is the marker, so a background refetch that answers the same
  // row cannot wipe out what the manager is half-way through typing (the same
  // rule the old «Квоты-лист» form applied, keyed on something stable at last).
  const seededFrom = useRef(venue.updatedAt);
  useEffect(() => {
    if (seededFrom.current === venue.updatedAt) return;
    seededFrom.current = venue.updatedAt;
    setManagerIds(venueManagerIds(venue));
    setControlStatus(venue.controlStatus ?? '');
    setAddress(venue.normalizedAddress ?? '');
    setCuratorName(venue.curatorName ?? '');
    setCuratorPhone(venue.curatorPhone ?? '');
    setCuratorEmail(venue.curatorEmail ?? '');
    setAllowCuratorChildren(venue.allowCuratorChildren === true);
    setDepartment(venue.department ?? '');
    setSheet(quotaSheetDraftFrom(venue));
  }, [venue]);

  const identity = venuePatchFrom(venue, {
    controlStatus,
    normalizedAddress: address,
    curatorName,
    curatorPhone,
    curatorEmail,
  });
  const managerIdsPatch = managerUserIdsPatchFrom(venueManagerIds(venue), managerIds);
  // `null` from the sheet diff means "the block is untouched" — only the
  // `{enrolledCount: 'invalid'}` sentinel is a refusal, and `null` is NOT one.
  const sheetPatch = quotaSheetPatchFrom(venue, sheet);
  const enrolledInvalid = sheetPatch !== null && !isVenuePatch(sheetPatch);
  const changedSheet = sheetPatch !== null && isVenuePatch(sheetPatch) && Object.keys(sheetPatch).length > 0 ? sheetPatch : null;
  const curatorFlagPatch = allowCuratorChildrenPatch(venue, allowCuratorChildren);
  const departmentPatch = venueDepartmentPatch(venue, department);
  const dirty = identity !== null || managerIdsPatch !== null || changedSheet !== null || curatorFlagPatch !== null || departmentPatch !== null;

  // ── WAVE 10 (D20): the explicit curator account ─────────────────────────────
  // The directory is already here (the manager picker loads it, 25.5) — the same
  // read answers "does this address have an account?". Both states are CLIENT-only:
  // the api has no flag field for "user not created", and the reminder recomputes
  // from the live data, so it can never go stale.
  const [curatorPromptOpen, setCuratorPromptOpen] = useState(false);
  const [curatorReminder, setCuratorReminder] = useState(false);
  const [curatorNotice, setCuratorNotice] = useState<string | null>(null);
  // T.Z29 — the SUCCESS notice of the create flow. It renders OUTSIDE the
  // prompt/reminder panel on purpose: the panel hides the moment the account is
  // linked (the address is accounted for now), and the sentence has to outlive it.
  const [curatorCreated, setCuratorCreated] = useState(false);
  const createCuratorUser = useCreateUser();
  const queryClient = useQueryClient();

  // T.Z23 — the busy state of the WHOLE flow, not of one request. `isPending` of a
  // single mutation goes down the moment that request answers, so between «account
  // created» and «#8 patch settled» the button used to wake up again, and a second
  // click fired a second POST — the 409 the owner saw. The ref is set synchronously
  // inside the handler, so the double-click's second event is dropped before any
  // React state could catch it; the state copy is what spins the button.
  const [curatorBusy, setCuratorBusy] = useState(false);
  const curatorFlowBusy = useRef(false);

  const curatorState = curatorAccountState({
    email: curatorEmail,
    directory,
    curatorUserIds: venue.curatorUserIds,
  });
  const promptThisSave = curatorPromptNeeded({
    storedEmail: venue.curatorEmail,
    draftEmail: curatorEmail,
    directory,
  });

  const curatorCreateDraft = curatorAccountCreateFrom({ email: curatorEmail, displayName: curatorName, venueId: venue.id });

  /** The #8 body the flow writes: the link set rides along with whatever the pane has staged. */
  const patchWithLink = (curatorUserIds: number[]): VenuePatch => ({
    ...(identity ?? {}),
    ...(managerIdsPatch ?? {}),
    ...(changedSheet ?? {}),
    ...(curatorFlagPatch ?? {}),
    ...(departmentPatch ?? {}),
    curatorUserIds,
  });

  /**
   * The identity (+sheet/flag/department) write — the legacy curator columns ride #8
   * like always. `closeAfter` is false on the D20 "not now" path: that save is
   * deliberately kept open so the reminder (and its create button) is what the
   * operator sees next.
   */
  const saveVenue = (curatorUserIds?: number[], closeAfter = true) => {
    const patch: VenuePatch =
      curatorUserIds !== undefined
        ? patchWithLink(curatorUserIds)
        : {
            ...(identity ?? {}),
            ...(managerIdsPatch ?? {}),
            ...(changedSheet ?? {}),
            ...(curatorFlagPatch ?? {}),
            ...(departmentPatch ?? {}),
          };
    // The link set alone is a legal write even when the fields went no-op (the venue
    // behind the pane refetched the address the first save had just stored).
    if (curatorUserIds === undefined && !dirty) return;
    if (Object.keys(patch).length === 0) return;
    edit.mutate(patch, {
      // A saved pane closes, the way every inline editor here used to: the card
      // behind it refetches and shows what the api stored.
      onSuccess: () => {
        if (closeAfter) onClose();
        else setCuratorReminder(true);
      },
    });
  };

  const save = () => {
    if (!dirty) return;
    // D20 — a save that SETS a curator address without an account stops here: the
    // drawer asks, it does not decide.
    if (promptThisSave && !curatorReminder) {
      setCuratorPromptOpen(true);
      return;
    }
    saveVenue();
  };

  /**
   * The directory, re-read and pushed into the cache. The cached read PREDATES the
   * account this flow just created (or found behind a 409), so the «Кураторы площадки»
   * block would resolve the new id to «№ N» until a reload — the second half of the
   * owner's finding. A failed re-read (an older api without 25.5, a dropped session)
   * falls back to the cached list and lets the invalidation retry: the refresh may not
   * be the thing that breaks the flow.
   */
  const refreshDirectory = async (email: string): Promise<number | null> => {
    try {
      const fresh = await fetchUserDirectory();
      queryClient.setQueryData(userDirectoryQueryKey(), fresh);
      return findCuratorAccount(fresh, email)?.id ?? null;
    } catch {
      void queryClient.invalidateQueries({ queryKey: userDirectoryQueryKey() });
      return findCuratorAccount(directory, email)?.id ?? null;
    }
  };

  /**
   * «Создать пользователя»: #25.1 (КУРАТОР + one link), then ONE #8 write with the link
   * set AND the text column.
   *
   * T.Z23 — three rules the first version lacked:
   *   * ONE flow at a time (the ref guard above), and the button stays disabled and
   *     spinning until the #8 patch has settled too;
   *   * a 409 on the POST is SUCCESS — the account exists, which is the goal. The id
   *     comes from a fresh directory read, because the 409 answer carries none;
   *   * both caches move while the pane is still up, so the new curator is named in
   *     «Кураторы площадки» (and in the card behind it) without a reload.
   *
   * T.Z29 — and the flow NEVER closes the pane: a success used to call `onClose()`
   * here, which threw away the very pane the operator was working in. The settled
   * branch is {@link curatorPaneAfterCreate} — the pane stays open with the created
   * name in «Кураторы площадки» and «Пользователь создан» beside it, and the save or
   * the close of the pane stays the operator's move.
   */
  const createAndSave = async () => {
    if (curatorFlowBusy.current) return;
    if (curatorCreateDraft.error !== undefined) return;
    curatorFlowBusy.current = true;
    setCuratorBusy(true);
    setCuratorNotice(null);
    setCuratorCreated(false);
    try {
      // The attempts are folded by the model so that a created row and a tolerated
      // duplicate land in the same "goal reached" place, whichever arrives first.
      const attempts: CuratorCreateAttempt[] = [];
      try {
        const created = await createCuratorUser.mutateAsync({
          email: curatorCreateDraft.email,
          displayName: curatorCreateDraft.displayName,
          role: 'VIEWER',
          venueIds: [],
          curatorVenueIds: [venue.id],
        });
        attempts.push({ ok: true, userId: created.id });
      } catch (cause) {
        if (!isCuratorEmailTaken(cause)) {
          setCuratorNotice(describeFailure(cause as ApiError | null, CURATOR_CREATE_FAILED_TEXT));
          return;
        }
        // 409 CONFLICT — «user with this email already exists». That is the account
        // this button promises, not an error: resolve it and go on to the link step.
        const resolved = await refreshDirectory(curatorCreateDraft.email);
        if (resolved !== null) attempts.push({ ok: true, userId: resolved });
        attempts.push({ ok: false, error: cause });
      }

      const outcome = curatorCreateOutcome(attempts);
      if (!outcome.ok) {
        setCuratorNotice(CURATOR_CREATE_FAILED_TEXT);
        return;
      }
      // The directory read answers the name (and, on the 409 path, the id). Still
      // unknown after it — a directory the api will not serve — is a stop, not a
      // silent link of a row the operator cannot see.
      if (outcome.userId === null) {
        const taken = curatorPaneAfterCreate({ linked: false, unresolved: true });
        setCuratorNotice(taken.notice?.text ?? null);
        if (taken.closePane) onClose();
        return;
      }

      try {
        await edit.mutateAsync(patchWithLink(curatorUserIdsWith(venue.curatorUserIds, outcome.userId)));
      } catch (cause) {
        setCuratorNotice(describeFailure(cause as ApiError | null, CURATOR_LINK_FAILED_TEXT));
        return;
      }

      // The server truth behind the pane: the #8 answer already updated the detail
      // cache in place, the awaited refetch is the cheap confirmation that the link
      // (and the legacy columns) really are what the api stored. Awaiting it — not
      // just firing it — is the point: the notice below may only appear once the
      // pane's «Кураторы площадки» block already names the created account, or the
      // success line would promise a curator the block has not shown yet. The
      // directory read (which the created path never had) is what resolves the name;
      // an id the client cannot resolve yet would print as «№ N».
      await refreshDirectory(curatorCreateDraft.email);
      await queryClient.invalidateQueries({ queryKey: venueDetailQueryKey(venue.id) });

      // T.Z29 — the success branch: notice + the name in the block, and the pane
      // STAYS OPEN. `closePane` is read, not assumed — the unit project pins the
      // whole table, and a future branch that should close would do it here.
      const settled = curatorPaneAfterCreate({ linked: true, unresolved: false });
      setCuratorPromptOpen(false);
      setCuratorReminder(false);
      setCuratorCreated(settled.notice !== null);
      if (settled.closePane) onClose();
    } finally {
      curatorFlowBusy.current = false;
      setCuratorBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Редактирование площадки"
      description={venue.name}
    >
      <div className="teacher-drawer" data-testid="venue-edit-pane">
        <section className="teacher-drawer__section" aria-label="Основное">
          <h4 className="teacher-drawer__title">Площадка</h4>
          <dl className="teacher-drawer__grid teacher-drawer__grid--single">
            <div>
              <dt>Регион</dt>
              <dd data-testid="venue-pane-region">{venue.region ?? MISSING}</dd>
            </div>
          </dl>

          <form
            className="venue-pane__form"
            data-testid="venue-edit"
            onSubmit={(event) => {
              event.preventDefault();
              save();
            }}
          >
            <div className="teacher-drawer__grid">
              <ManagerEditor
                directory={directory}
                assignedIds={managerIds}
                onAdd={(userId) => setManagerIds((current) => (current.includes(userId) ? current : [...current, userId]))}
                onRemove={(userId) => setManagerIds((current) => current.filter((id) => id !== userId))}
              />
              <Field label="Статус контроля" htmlFor="venue-edit-control" hint="Пусто — очистить">
                <Input
                  id="venue-edit-control"
                  data-testid="venue-edit-control-status"
                  value={controlStatus}
                  onChange={(event) => setControlStatus(event.target.value)}
                />
              </Field>
              {/* T.Z94 (v1.9.15) — the venue's OWN department. When set it wins over the
                  managers' fold (the T.Z56 derivation) in every «Отдел» consumer; «не
                  задано» hands the decision back to the managers. The api audits the
                  write like every other key of this form. */}
              <Field
                label="Отдел"
                htmlFor="venue-edit-department"
                hint="Задан — перекрывает отдел менеджеров площадки; «не задано» — считать по менеджерам"
              >
                <Select
                  id="venue-edit-department"
                  data-testid="venue-edit-department"
                  value={department}
                  onChange={(event) => setDepartment(event.target.value)}
                >
                  {venueDepartmentSelectOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Адрес (нормализованный)"
                htmlFor="venue-edit-address"
                hint="Ключ сопоставления — api нормализует значение перед сохранением"
              >
                <Input
                  id="venue-edit-address"
                  data-testid="venue-edit-address"
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                />
              </Field>
              {/* A8 (T.Z12) — the three «Квоты»-sheet columns, each carrying the hint
                  that keeps them from reading as an access grant: they are contact
                  text the seed imports, the block below is the accounts. */}
              <Field label="Куратор — ФИО" htmlFor="venue-edit-curator-name" hint={`${CURATOR_LEGACY_HINT} · пусто — очистить`}>
                <Input
                  id="venue-edit-curator-name"
                  data-testid="venue-edit-curator-name"
                  value={curatorName}
                  onChange={(event) => setCuratorName(event.target.value)}
                />
              </Field>
              <Field label="Куратор — телефон" htmlFor="venue-edit-curator-phone" hint={`${CURATOR_LEGACY_HINT} · как в реестре`}>
                <Input
                  id="venue-edit-curator-phone"
                  data-testid="venue-edit-curator-phone"
                  value={curatorPhone}
                  onChange={(event) => setCuratorPhone(event.target.value)}
                />
              </Field>
              <Field
                label="Куратор — почта"
                htmlFor="venue-edit-curator-email"
                hint={`${CURATOR_LEGACY_HINT} · сохраняется строчными буквами. ${CURATOR_OLD_LINK_HINT}`}
              >
                <Input
                  id="venue-edit-curator-email"
                  data-testid="venue-edit-curator-email"
                  type="email"
                  value={curatorEmail}
                  onChange={(event) => setCuratorEmail(event.target.value)}
                />
              </Field>
            </div>

            {/* WAVE 10 (D20) — the explicit account. The prompt fires on a save that
                would SET a curator address without an `app_users` row; the reminder is
                the same panel after a «нет», and both are CLIENT state only. */}
            {(curatorPromptOpen || curatorState === 'missing' || curatorReminder) && curatorEmail.trim() !== '' ? (
              <div
                className="venue-edit__curator-account"
                data-testid={curatorPromptOpen ? 'venue-edit-curator-prompt' : 'venue-edit-curator-reminder'}
                role="group"
                aria-label={curatorPromptOpen ? CURATOR_PROMPT_TITLE : CURATOR_REMINDER_TITLE}
              >
                <p className="venue-edit__curator-title" data-testid="venue-edit-curator-account-title">
                  {curatorPromptOpen ? CURATOR_PROMPT_TITLE : CURATOR_REMINDER_TITLE}
                </p>
                <p className="venue-edit__curator-hint">
                  {curatorPromptOpen ? CURATOR_PROMPT_HINT : CURATOR_REMINDER_HINT}
                </p>
                <p className="venue-edit__curator-hint">{CURATOR_MANAGER_NOTE}</p>
                {curatorCreateDraft.error === 'empty-name' ? (
                  <p className="field__error" data-testid="venue-edit-curator-name-required">
                    {CURATOR_NAME_REQUIRED}
                  </p>
                ) : null}
                {curatorNotice ? (
                  <p className="field__error" role="alert" data-testid="venue-edit-curator-account-error">
                    {curatorNotice}
                  </p>
                ) : null}
                <div className="venue-edit__curator-actions">
                  {/* T.Z23 — `curatorBusy` spans BOTH writes of the flow (#25.1 then the
                      #8 patch), so the button is disabled and spinning for as long as
                      clicking it would mean anything: the spinner is the answer to
                      «ничего не происходит» the first click used to leave open. */}
                  <Button
                    size="sm"
                    variant="primary"
                    data-testid="venue-edit-curator-create"
                    loading={curatorBusy}
                    disabled={curatorCreateDraft.error !== undefined || curatorBusy}
                    onClick={() => void createAndSave()}
                  >
                    {CURATOR_CREATE_BUTTON}
                  </Button>
                  {curatorPromptOpen ? (
                    <Button
                      size="sm"
                      data-testid="venue-edit-curator-skip"
                      disabled={curatorBusy}
                      onClick={() => {
                        setCuratorPromptOpen(false);
                        setCuratorReminder(true);
                        saveVenue(undefined, false);
                      }}
                    >
                      {CURATOR_SKIP_BUTTON}
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* T.Z29 — the create flow's success line. It sits OUTSIDE the prompt /
                reminder panel above on purpose: that panel hides as soon as the
                address is accounted for, and this sentence has to outlive it. The
                created curator is named in the block right below. */}
            {curatorCreated ? (
              <p className="venue-edit__curator-success" role="status" data-testid="venue-edit-curator-created">
                {CURATOR_CREATED_TEXT}
              </p>
            ) : null}

            {/* A8 (T.Z12) — the LINKED curator accounts, the half the sheet columns
                above cannot show. Read-only: the set is written by #8
                `curatorUserIds`, and the D20 prompt above is the flow that grows it
                from this drawer; removing one stays with «Куратор площадок» on the
                users page, which is the only other editor the contract leaves. */}
            <div className="venue-pane__curators" data-testid="venue-curator-users">
              <h4 className="teacher-drawer__title">{CURATOR_USERS_TITLE}</h4>
              <p className="venue-pane__curators-hint">{CURATOR_USERS_HINT}</p>
              {curatorUsers.length === 0 ? (
                <p className="venue-edit__managers-empty" data-testid="venue-curator-users-empty">
                  {CURATOR_USERS_EMPTY}
                </p>
              ) : (
                <ul className="venue-pane__curator-list" data-testid="venue-curator-users-list">
                  {curatorUsers.map((user) => (
                    <li className="venue-pane__curator" key={user.id} data-testid="venue-curator-user" data-user-id={user.id}>
                      <span className="venue-pane__curator-name" title={user.name}>
                        {user.name}
                      </span>
                      {user.email ? <span className="venue-pane__curator-email">{user.email}</span> : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Wave 9 (T.Y9, D3) — the right is a property of the venue, not of the
                curator's label: ON here gives every curator of this venue the online
                prefill surface, OFF takes it away (the offline block is read-only). */}
            <label className="venue-edit__flag" htmlFor="venue-edit-allow-curator-children">
              <input
                id="venue-edit-allow-curator-children"
                type="checkbox"
                data-testid="venue-edit-allow-curator-children"
                checked={allowCuratorChildren}
                onChange={(event) => setAllowCuratorChildren(event.currentTarget.checked)}
              />
              <span>
                Разрешить куратору вносить детей (онлайн-набор)
                <span className="venue-edit__flag-hint">
                  Онлайн-набор ведёт куратор площадки; без флага его кабинет показывает только офлайн-блок.
                </span>
              </span>
            </label>

            <h4 className="teacher-drawer__title venue-pane__sheet-title">Квоты-лист</h4>
            <div className="teacher-drawer__grid">
              {quotaSheetRows(venue).map((row) => {
                const key = row.editable;
                if (key === undefined) return null;
                return (
                  <Field key={row.testId} label={row.label} htmlFor={`venue-pane-${row.testId}`}>
                    <Input
                      id={`venue-pane-${row.testId}`}
                      data-testid={`venue-pane-${row.testId}`}
                      value={sheet[key]}
                      inputMode={key === 'enrolledCount' ? 'numeric' : undefined}
                      onChange={(event) => setSheet((current) => ({ ...current, [key]: event.target.value }))}
                    />
                  </Field>
                );
              })}
            </div>
            <p className="teacher-drawer__hint">
              «Выбор программ» остаётся за импортом: из него считается разбивка онлайн-квот.
            </p>

            {enrolledInvalid ? (
              <p className="field__error" role="alert" data-testid="venue-edit-error">
                «Кол-во зачисленных» — целое число или пустое поле.
              </p>
            ) : edit.isError ? (
              <p className="field__error" role="alert" data-testid="venue-edit-error">
                {describeFailure(edit.error as ApiError | null, 'Не удалось сохранить изменения.')}
              </p>
            ) : null}

            <div className="venue-pane__actions">
              <Button size="sm" data-testid="venue-edit-cancel" onClick={onClose}>
                Отмена
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={edit.isPending}
                disabled={!dirty || enrolledInvalid}
                data-testid="venue-edit-save"
                onClick={save}
              >
                Сохранить
              </Button>
              <span className="venue-edit__audit-hint">{AUDIT_HINT}</span>
            </div>
          </form>
        </section>
      </div>
    </Drawer>
  );
}

/**
 * `quotaSheetPatchFrom` answers the patch, `null` for "nothing changed" — or the
 * `{enrolledCount: 'invalid'}` sentinel when the one numeric column holds prose.
 */
function isVenuePatch(value: VenuePatch | { enrolledCount: 'invalid' } | null): value is VenuePatch {
  return value !== null && !(typeof value === 'object' && 'enrolledCount' in value && value.enrolledCount === 'invalid');
}

/**
 * The manager editor of the pane (Z3) — a staged multi-select over
 * `managerUserIds`. Every assigned manager is a chip with its own «Убрать», the
 * combobox offers only the users not on the venue yet, and the WHOLE set travels
 * in the one #8 PATCH on save (a REPLACE-SET — the same shape `PUT
 * /users/:id/venues` mirrors). The deprecated scalar this replaces answered
 * `managerUserIds[0]`, so removing a non-first manager was a no-op and a clear
 * would have silently dropped all the others.
 */
function ManagerEditor({
  directory,
  assignedIds,
  onAdd,
  onRemove,
}: {
  directory: ReadonlyArray<UserDirectoryEntry>;
  assignedIds: ReadonlyArray<number>;
  onAdd: (userId: number) => void;
  onRemove: (userId: number) => void;
}) {
  const assigned = assignedIds.map((id) => ({ id, label: managerDisplayLabel(id, directory) }));
  // Only the users not on the venue are addable — and the moment one is chosen it
  // leaves the options, so the input clears itself instead of showing the just-added
  // name as a second, competing selection.
  const options = managerComboboxOptions(directory.filter((entry) => !assignedIds.includes(entry.id)));

  return (
    <Field
      label="Менеджеры"
      htmlFor="venue-edit-manager"
      hint="Найдите по имени или e-mail. «Убрать» снимает одного менеджера — остальные сохраняются."
    >
      <div className="venue-edit__managers" data-testid="venue-edit-managers">
        {assigned.length === 0 ? (
          <p className="venue-edit__managers-empty" data-testid="venue-edit-managers-empty">
            Не назначены
          </p>
        ) : (
          assigned.map((manager) => (
            <span className="venue-edit__manager" key={manager.id} data-testid="venue-edit-manager-chip" data-user-id={manager.id}>
              <span className="venue-edit__manager-name" title={manager.label}>
                {manager.label}
              </span>
              <button
                type="button"
                className="icon-btn icon-btn--sm"
                aria-label={`Убрать менеджера ${manager.label}`}
                data-testid={`venue-edit-manager-remove-${manager.id}`}
                onClick={() => onRemove(manager.id)}
              >
                ×
              </button>
            </span>
          ))
        )}
        <Combobox
          id="venue-edit-manager"
          testId="venue-edit-manager"
          value=""
          options={options}
          onChange={(value) => {
            const userId = Number.parseInt(value, 10);
            if (Number.isFinite(userId)) onAdd(userId);
          }}
          placeholder={assigned.length === 0 ? 'Не назначен' : 'Добавить менеджера'}
          emptyText="Пользователь не найден"
          clearLabel="Очистить"
          ariaLabel="Менеджер площадки"
          resultsLabel={(count) => `Найдено пользователей: ${count}`}
        />
      </div>
    </Field>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/**
 * «Онлайн-набор» (T.Z27) — the door from the venue card to the venue's own prefill
 * surface (`/venues/:venueId/prefill`), where the SAME cabinet table a curator uses
 * is scoped to this one venue for the staff who keep it.
 *
 * The section follows the api's own answer, not a local opinion about who may prefill:
 * `venuePrefillAccess` reads the venue's flag plus the session's published cabinet
 * scope (`user.cabinetVenueIds`) and hides the whole thing whenever the api would
 * refuse — a venue without the flag has no set to keep, a manager not linked to this
 * venue is 403 there, and a КУРАТОР-role session never reaches the staff card at all
 * (their surface is `/cabinet`). Hiding is the honest rendering: the page behind the
 * link repeats the same refusal in words for a hand-typed URL.
 */
function VenuePrefillSection({
  venue,
  role,
  cabinetVenueIds,
}: {
  venue: VenueDetailDto;
  role: ReturnType<typeof useAuth>['role'];
  cabinetVenueIds: ReturnType<typeof useAuth>['cabinetVenueIds'];
}) {
  const access = venuePrefillAccess({
    venueId: venue.id,
    allowCuratorChildren: venue.allowCuratorChildren === true,
    role,
    cabinetVenueIds,
  });
  if (access !== 'open') return null;

  return (
    <section className="card venue-section" data-testid="venue-prefill">
      <header className="venue-section__head">
        <h2 className="venue-section__title">Онлайн-набор</h2>
        <span className="venue-section__hint">Внесение детей открыто — набор ведут назначенные менеджеры</span>
      </header>
      <div className="venue-section__body">
        <p className="venue-prefill__hint">
          Строки этой площадки: добавить ребёнка, править и снимать несопоставленные, скачать xlsx. Формат ФИО и
          дубликаты проверяет сервер.
        </p>
        <Link className="link-btn venue-prefill__link" to={venuePrefillPath(venue.id)} data-testid="venue-prefill-open">
          Открыть онлайн-набор
        </Link>
      </div>
    </section>
  );
}

function StatBlock({ num, label, testId }: { num: ReactNode; label: string; testId: string }) {
  return (
    <div className="venue-stat" data-testid={testId}>
      <span className="venue-stat__num">{num}</span>
      <span className="venue-stat__label">{label}</span>
    </div>
  );
}

/**
 * «Педагоги» (T.R10) — the venue's own slice of the #7.5 registry, listed where the
 * manager is already looking instead of only as a number in the stat strip. The read is
 * `GET /teachers?venueId=`, which the contract gates for WRITES only — the roster is
 * PII-clear to every role, so the section renders for a VIEWER too, and a refusal is a
 * muted line rather than a broken card.
 *
 * The list is the first {@link TEACHERS_PREVIEW_LIMIT} ФИО in the api's own order (venue,
 * then ФИО); a name opens the T.R7 drawer on that row (`?venueId=…&teacherId=…`), and
 * everything past the cut is one «ещё N» link into the venue-filtered roster — the same
 * destination the «Педагоги» stat above points at.
 */
function VenueTeachers({ venueId }: { venueId: number }) {
  const roster = useTeachers({ venueId });
  const teachers: TeacherDto[] = roster.data?.teachers ?? [];
  const total = roster.data?.meta?.total ?? teachers.length;
  const visible = teachers.slice(0, TEACHERS_PREVIEW_LIMIT);
  const rest = Math.max(total - visible.length, 0);

  return (
    <section className="card venue-section" data-testid="venue-teachers">
      <header className="venue-section__head">
        <h2 className="venue-section__title">Педагоги</h2>
        <Link className="venue-section__link" to={teachersRosterPath(venueId)} data-testid="venue-teachers-all">
          Все педагоги
        </Link>
      </header>
      <div className="venue-section__body">
        {roster.isPending ? (
          <p className="venue-teachers__empty" data-testid="venue-teachers-loading">
            Загружаем педагогов площадки…
          </p>
        ) : roster.isError ? (
          <p className="venue-teachers__empty" data-testid="venue-teachers-error">
            {describeFailure(roster.error as ApiError | null, 'Не удалось загрузить педагогов площадки.')}
          </p>
        ) : visible.length === 0 ? (
          <EmptyState
            title="Педагогов нет"
            hint="Реестр педагогов пока не привязал к этой площадке ни одного педагога."
          />
        ) : (
          <>
            <ul className="venue-teachers" data-testid="venue-teachers-list">
              {visible.map((teacher) => (
                <li key={teacher.id}>
                  <Link to={teachersRosterPath(venueId, teacher.id)} data-testid="venue-teacher">
                    {teacher.fullName}
                  </Link>
                </li>
              ))}
            </ul>
            {rest > 0 ? (
              <Link className="venue-teachers__more" to={teachersRosterPath(venueId)} data-testid="venue-teachers-more">
                ещё {teacherCountLabel(rest)}
              </Link>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * «Квоты» — the venue's OWN two numbers (T.V2, on `venue_plans` since T.V1).
 *
 * Two stat-like blocks side by side, the way the owner's sheet reads: «Офлайн N» and
 * «Онлайн M», independent of any programme. An ADMIN gets the edit affordance (the same
 * PATCH #8 write the quota grid uses — the numbers are one value, wherever they are
 * edited); a VIEWER reads the numbers. Beside each plan sits «занято (факт)», read-only
 * and labelled as reference information:
 *   * offline fact = the last point of #7's `offlineFactTrend` (the freshest ЕПГУ cut);
 *   * online fact = the venue's fill-counted online total off the online pivot (#8.5).
 * Both numerators exclude «Отказ» (T.Z44 — «проценты заполняемости без Отказа»): an
 * application the campaign refused never occupied a seat, so it is on neither side of the
 * fact-vs-plan reading this section invites.
 * The facts are DERIVED, the plans are not — which is why only the plans are editable.
 */
function VenuePlans({ venue, canAdmin }: { venue: VenueDetailDto; canAdmin: boolean; onEdit?: () => void }) {
  // The online pivot (the «занято (факт)» reference number) — the hook is taken
  // UNCONDITIONALLY: a hook inside the editing branch would reorder on the first edit
  // and React would tear the tree down.
  const pivot = useOnlinePivot();
  const onlineFact = onlineFactOf(pivot, venue.id);
  // The inline edit (ADMIN): the same PATCH #8 write the quota grid uses, so the two
  // numbers are one value wherever they are edited. The drawer stays the place for the
  // curator fields; the plan is edited where it is read.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ offline: '', online: '' });
  const save = usePatchVenue(venue.id);

  const open = () => {
    setDraft({ offline: String(venue.planOffline), online: String(venue.planOnline) });
    setEditing(true);
  };

  const parsed = {
    offline: parsePlanInput(draft.offline),
    online: parsePlanInput(draft.online),
  };
  const invalid =
    (draft.offline !== '' && parsed.offline.error !== undefined) ||
    (draft.online !== '' && parsed.online.error !== undefined);
  const changed =
    (parsed.offline.value !== null && parsed.offline.value !== venue.planOffline) ||
    (parsed.online.value !== null && parsed.online.value !== venue.planOnline);

  const commit = () => {
    const body: VenuePatch = {};
    if (parsed.offline.value !== null && parsed.offline.value !== venue.planOffline) body.planOffline = parsed.offline.value;
    if (parsed.online.value !== null && parsed.online.value !== venue.planOnline) body.planOnline = parsed.online.value;
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    save.mutate(body, { onSuccess: () => setEditing(false) });
  };

  return (
    <section className="card venue-section" data-testid="venue-quotas">
      <header className="venue-section__head">
        <h2 className="venue-section__title">Квоты</h2>
        <span className="venue-section__hint">План площадки по форматам · офлайн и онлайн независимы</span>
      </header>
      <div className="venue-section__body">
        {editing ? (
          <form
            className="venue-plans venue-plans--edit"
            data-testid="venue-plan-form"
            onSubmit={(event) => {
              event.preventDefault();
              commit();
            }}
          >
            <Field label="План офлайн" htmlFor="venue-plan-offline-input" error={parsed.offline.error}>
              <Input
                id="venue-plan-offline-input"
                data-testid="venue-plan-input-offline"
                inputMode="numeric"
                value={draft.offline}
                onChange={(event) => setDraft((current) => ({ ...current, offline: event.target.value }))}
              />
            </Field>
            <Field label="План онлайн" htmlFor="venue-plan-online-input" error={parsed.online.error}>
              <Input
                id="venue-plan-online-input"
                data-testid="venue-plan-input-online"
                inputMode="numeric"
                value={draft.online}
                onChange={(event) => setDraft((current) => ({ ...current, online: event.target.value }))}
              />
            </Field>
            <div className="venue-plans__actions">
              <Button type="submit" size="sm" variant="primary" data-testid="venue-plan-save" disabled={invalid || !changed} loading={save.isPending}>
                Сохранить
              </Button>
              <Button type="button" size="sm" data-testid="venue-plan-cancel" onClick={() => setEditing(false)}>
                Отмена
              </Button>
              {save.isError ? (
                <span className="field__error" role="alert" data-testid="venue-plan-error">
                  {describeFailure(save.error as ApiError | null, 'Не удалось сохранить план.')}
                </span>
              ) : null}
            </div>
          </form>
        ) : (
          <>
            <div className="venue-plans">
              <PlanFigure tone="offline" testId="venue-plan-offline" label="План офлайн" plan={venue.planOffline} fact={offlineFactOf(venue)} />
              <PlanFigure tone="online" testId="venue-plan-online" label="План онлайн" plan={venue.planOnline} fact={onlineFact} />
            </div>
            <p className="venue-plans__total" data-testid="venue-plan-total">
              Итого по площадке: {formatCount(venue.planOffline + venue.planOnline)} мест
            </p>
            {canAdmin ? (
              <p className="venue-plans__edit">
                <button type="button" className="link-btn" data-testid="venue-plan-edit" onClick={open}>
                  Изменить план
                </button>{' '}
                <span className="venue-plans__edit-hint">сохраняется через #8 и попадает в аудит</span>
              </p>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

/** One plan figure: the number big, the derived fact small beside it. */
function PlanFigure({
  tone,
  label,
  plan,
  fact,
  testId,
}: {
  tone: 'offline' | 'online';
  label: string;
  plan: number;
  /** `null` = the fact cannot be derived for this venue (no cut, no online rows). */
  fact: number | null;
  testId: string;
}) {
  return (
    <div className={`venue-plan venue-plan--${tone}`} data-testid={testId}>
      <span className="venue-plan__label">{label}</span>
      <span className="venue-plan__value" data-testid={`${testId}-value`}>
        {formatCount(plan)}
      </span>
      <span className="venue-plan__fact" data-testid={`${testId}-fact`}>
        {fact === null ? (
          <span className="cell-muted">факт (справочно): —</span>
        ) : (
          <>
            занято (факт, справочно): <strong>{formatCount(fact)}</strong>
          </>
        )}
      </span>
    </div>
  );
}

/**
 * «Онлайн-квота партнёра» (wave 9, T.Y9) — the partner's SHARED pool, shown on
 * every branch of that partner and READ-ONLY here (SR §5.3: «онлайн-квота
 * партнёра (read-only, правится в #29)»). The one editor lives in Settings →
 * «Онлайн-квота партнёра», so the pool's audit trail has one address and fifteen
 * branches cannot look like fifteen independent quotas.
 */
function PartnerPoolBlock({ venue }: { venue: VenueDetailDto }) {
  return (
    <section className="card venue-section" data-testid="venue-partner-pool">
      <header className="venue-section__head">
        <h2 className="venue-section__title">Онлайн-квота партнёра</h2>
        <span className="venue-section__hint">
          Общий пул партнёра{venue.partnerName ? ` «${venue.partnerName}»` : ''} — его читают все филиалы
        </span>
      </header>
      <div className="venue-section__body">
        <div className="venue-plan venue-plan--online" data-testid="venue-partner-pool-value">
          <span className="venue-plan__label">Пул партнёра</span>
          <span className="venue-plan__value">{formatCount(partnerPoolOf(venue))}</span>
          <span className="venue-plan__fact cell-muted">
            правится в Настройках → «Онлайн-квота партнёра» (#29)
          </span>
        </div>
      </div>
    </section>
  );
}

/** The hover text of the kind badge — the two kinds, in the SR's own words. */
function venueKindTitle(kind: VenueKind | undefined, partnerName: string | null | undefined): string {
  return kind === 'PARTNER_ONLINE'
    ? `Филиал партнёра${partnerName ? ` «${partnerName}»` : ''} — план читается по общему пулу партнёра`
    : 'Реестровая площадка офлайн-набора';
}

/** The freshest offline fact: the LAST point of the ЕПГУ trend, `null` when there is none. */
function offlineFactOf(venue: VenueDetailDto): number | null {
  const trend = Array.isArray(venue.offlineFactTrend) ? venue.offlineFactTrend : [];
  const last = trend.at(-1);
  return last === undefined ? null : last.count;
}

/**
 * The online fact: the venue's own ONLINE applications that COUNT toward the fill (T.Z44 —
 * everything except «Отказ», the pivot's `fillCounted`), falling back to the row's `total`
 * while an older api has not answered the field yet. `null` when the pivot is not there yet
 * or holds no row for the venue. A payload whose `rows` is not an array (a stub, a partial
 * refetch) reads as "no fact" — never as a crash.
 */
function onlineFactOf(
  pivot: { data?: { rows?: ReadonlyArray<{ venueId: number | null; fillCounted?: number; total: number }> } },
  venueId: number,
): number | null {
  const rows = pivot.data?.rows;
  if (!Array.isArray(rows)) return null;
  const row = rows.find((entry) => entry.venueId === venueId);
  if (row === undefined) return null;
  return typeof row.fillCounted === 'number' && Number.isFinite(row.fillCounted) ? row.fillCounted : row.total;
}

/**
 * «Выбор программ» (T.W3) — the sheet's own cell as a read-only block of its
 * own, next to the quota grid it qualifies.
 *
 * Why a section and not a «Квоты-лист» row: the cell is a venue's PRIORITY list
 * (the programmes it wants, enumerated one per line), and the manager reads it
 * while talking about quotas — but it is not one. The header says so
 * ({@link PROGRAMS_SELECTION_HINT}), and the seed's own words are kept as the
 * lines of the list (a line break is data here, docs/02 §6). The field is
 * seed-owned (`Venue.programsSelection`), so there is deliberately no edit: the
 * next import would overwrite it.
 *
 * An empty cell is a fact a manager acts on («the venue has not chosen yet»),
 * so it gets its own sentence rather than an em dash.
 */
function ProgramsSelection({ venue }: { venue: VenueDetailDto }) {
  const lines = programsSelectionLines(venue.programsSelection);

  return (
    <section className="card venue-section" data-testid="venue-programs-selection">
      <header className="venue-section__head">
        <h2 className="venue-section__title">Выбор программ</h2>
        <span className="venue-section__hint">{PROGRAMS_SELECTION_HINT}</span>
      </header>
      <div className="venue-section__body">
        {lines.length === 0 ? (
          <EmptyState
            title={PROGRAMS_SELECTION_EMPTY_TITLE}
            hint="Лист «Квоты» не принёс «Выбор программ» для этой площадки."
          />
        ) : (
          <ul className="venue-programs" data-testid="venue-programs-list">
            {lines.map((line, index) => (
              // The lines are the value itself (a priority order), not keyed
              // rows — the position is the only identity a sheet line has.
              <li key={`${index}-${line}`} data-testid="venue-programs-line">
                {line}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/**
 * «Факт офлайн по датам» (T.W5) — the offline fact the model can honestly
 * speak about, derived per ЕПГУ snapshot date by the api (#7's additive
 * `offlineFactTrend`): how many of the venue's OFFLINE applications each cut
 * already carried.
 *
 * A handful of mini bars, not a chart: the section's whole job is to answer
 * «is the venue's offline number still moving between the cuts?». There is no
 * online counterpart on purpose (T.W4) — the sheet's «Факт онлайн» is
 * 1-of-191 filled and online stays plan-only until the 14.09 re-cut — so the
 * header says «офлайн» and means it.
 */
function OfflineTrend({ venue }: { venue: VenueDetailDto }) {
  const bars = offlineTrendBars(venue.offlineFactTrend);

  return (
    <section className="card venue-section" data-testid="venue-offline-trend">
      <header className="venue-section__head">
        <h2 className="venue-section__title">{OFFLINE_TREND_TITLE}</h2>
        <span className="venue-section__hint">{OFFLINE_TREND_HINT}</span>
      </header>
      <div className="venue-section__body">
        {bars.length === 0 ? (
          <EmptyState
            title={OFFLINE_TREND_EMPTY_TITLE}
            hint="Ни один срез ЕПГУ не принёс офлайн-заявлений этой площадки."
          />
        ) : (
          <div className="venue-trend" data-testid="venue-trend-chart">
            {bars.map((bar) => (
              <div className="venue-trend__col" key={bar.title} data-testid="venue-trend-bar" data-count={bar.count} title={bar.title}>
                <span className="venue-trend__count">{formatCount(bar.count)}</span>
                <span className="venue-trend__track">
                  <span className="venue-trend__bar" style={{ height: `${bar.heightPercent}%` }} />
                </span>
                <span className="venue-trend__date">{bar.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * The link registry (#22.5, T.F2). Rows read off #7 (whose `id`/`format` make a
 * row addressable since v1.3); the ADMIN edits inline, adds and deletes —
 * every write optimistic, every failure rolled back, the 409 of a clashing
 * url/slot named in the form.
 */
function VenueLinks({ venue, canEdit }: { venue: VenueDetailDto; canEdit: boolean }) {
  const create = useCreateVenueLink(venue.id, venue.name);
  const patch = usePatchVenueLink(venue.id);
  const remove = useDeleteVenueLink(venue.id);
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  // The add form is a disclosure (wave 6, owner): a venue holds a handful of
  // links it almost never changes, so a permanently open four-field form sat
  // between the table and the next section on every visit.
  const [adding, setAdding] = useState(false);
  // Bumped by every successful add: the key remounts the form, so the next
  // entry starts from an empty draft instead of the row just written.
  const [addNonce, setAddNonce] = useState(0);

  const createError = create.isError ? linkErrorText(create.error as ApiError | null) : null;
  const patchError = patch.isError ? linkErrorText(patch.error as ApiError | null) : null;
  const removeError = remove.isError ? linkErrorText(remove.error as ApiError | null, 'Не удалось удалить ссылку.') : null;

  return (
    <section className="card venue-section" data-testid="venue-links">
      <header className="venue-section__head">
        <h2 className="venue-section__title">Реестр ссылок</h2>
        <span className="venue-section__hint">Ссылки ЕПГУ, которые ведут на эту площадку</span>
      </header>
      <div className="venue-section__body">
        {venue.links.length === 0 ? (
          <EmptyState
            title="Ссылок нет"
            hint={canEdit ? 'Добавьте первую ссылку — по ней реестр связывает площадку с программой.' : 'Реестр ссылок пока не связал эту площадку с программами.'}
          />
        ) : (
          <TableWrap>
            <table className="data" data-testid="link-table">
              <thead>
                <tr>
                  <th>Ссылка</th>
                  <th>Курс</th>
                  <th>Позиция</th>
                  <th>Формат</th>
                  {canEdit ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {venue.links.map((link) =>
                  canEdit && editingId === link.id ? (
                    <tr key={link.id} data-testid="link-edit-row" data-link-id={link.id}>
                      <td colSpan={5}>
                        <LinkForm
                          testIdPrefix="link-edit"
                          form={linkFormFrom(link)}
                          pending={patch.isPending}
                          conflict={patchError}
                          onCancel={() => {
                            patch.reset();
                            setEditingId(null);
                          }}
                          onSubmit={(form) => {
                            const body = linkPatchFrom(link, form);
                            if (!body) return;
                            patch.mutate(
                              { linkId: link.id, patch: body },
                              {
                                onSuccess: () => {
                                  patch.reset();
                                  setEditingId(null);
                                },
                              },
                            );
                          }}
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr key={link.id} data-testid="venue-link-row" data-link-id={link.id}>
                      <td data-testid="venue-link-url">
                        <a href={link.url} target="_blank" rel="noreferrer">
                          {link.url}
                        </a>
                      </td>
                      <td className="num" data-testid="venue-link-course">
                        {link.courseId}
                      </td>
                      <td className="num" data-testid="venue-link-place">
                        {link.placeId}
                      </td>
                      <td data-testid="venue-link-format">{link.format ? formatLabel(link.format) : '—'}</td>
                      {canEdit ? (
                        <td className="row-actions">
                          <Button
                            size="sm"
                            data-testid="link-edit"
                            onClick={() => {
                              patch.reset();
                              setConfirmingId(null);
                              setEditingId(link.id);
                            }}
                          >
                            Изменить
                          </Button>
                          {confirmingId === link.id ? (
                            <>
                              <Button
                                variant="danger"
                                size="sm"
                                loading={remove.isPending && remove.variables === link.id}
                                data-testid="link-delete-confirm"
                                onClick={() => remove.mutate(link.id, { onSuccess: () => setConfirmingId(null) })}
                              >
                                Подтвердить
                              </Button>
                              <Button size="sm" variant="ghost" data-testid="link-delete-cancel" onClick={() => setConfirmingId(null)}>
                                Отмена
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="danger"
                              size="sm"
                              data-testid="link-delete"
                              onClick={() => setConfirmingId(link.id)}
                            >
                              Удалить
                            </Button>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </TableWrap>
        )}

        {removeError ? (
          <p className="field__error" role="alert" data-testid="link-delete-error">
            {removeError}
          </p>
        ) : null}

        {canEdit ? (
          adding ? (
            <div className="link-add">
              <LinkForm
                key={`add-${addNonce}`}
                testIdPrefix="link-add"
                form={EMPTY_LINK_FORM}
                pending={create.isPending}
                conflict={createError}
                submitLabel="Добавить ссылку"
                onCancel={() => {
                  create.reset();
                  setAdding(false);
                }}
                onSubmit={(form) => {
                  const body = linkCreateFrom(form);
                  if (!body) return;
                  create.mutate(body, {
                    onSuccess: () => {
                      create.reset();
                      setAddNonce((nonce) => nonce + 1);
                      setAdding(false);
                    },
                  });
                }}
              />
            </div>
          ) : (
            <div className="section-add">
              <Button
                size="sm"
                variant="secondary"
                data-testid="link-add-toggle"
                aria-expanded={adding}
                onClick={() => setAdding(true)}
              >
                + Добавить ссылку
              </Button>
            </div>
          )
        ) : null}

        {canEdit ? (
          <p className="venue-edit__audit-hint" data-testid="link-audit-hint">
            {AUDIT_HINT}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/**
 * «Квоты-лист» (T.R9) — every «Квоты»-sheet attribute the seed imports, in the
 * sheet's own column order and with its own words for the labels. The manager's
 * working sheet lives in the DB, so it is readable here instead of only in the
 * CSV hand-over.
 *
 * Read-only rows are the columns the SEED owns (the T.F7 block): an edit would
 * be overwritten by the next import, so the card does not pretend otherwise.
 * The three #8-accepts fields («Проверка выгодных кейсов», the curator report
 * link, «Дополнительное согласование») are editable for the ADMIN in place —
 * one PATCH for the whole section, only the changed keys travel, audited like
 * every #8 write.
 */
/**
 * «Квоты-лист» (T.R9) — every «Квоты»-sheet attribute the seed imports, in the
 * sheet's own column order and with its own words for the labels. The manager's
 * working sheet lives in the DB, so it is readable here instead of only in the
 * CSV hand-over.
 *
 * READ-ONLY since wave 6: the editing used to be an inline form on three of the
 * eight rows, which read as "the other five are not ours to correct" — and the
 * owner was keeping a private copy of the sheet to do exactly that. The rows are
 * #8-accepts now (contract v1.5), and the edit lives in the right pane.
 */
function QuotaSheetSection({ venue, canAdmin, onEdit }: { venue: VenueDetailDto; canAdmin: boolean; onEdit: () => void }) {
  const rows = quotaSheetRows(venue);

  return (
    <section className="card venue-section" data-testid="venue-quota-sheet">
      <header className="venue-section__head">
        <h2 className="venue-section__title">Квоты-лист</h2>
        <span className="venue-section__hint">Колонки листа «Квоты» — импортируются при загрузке</span>
      </header>
      <div className="venue-section__body">
        {canAdmin ? (
          <div className="venue-sheet__actions">
            <Button size="sm" data-testid="sheet-edit-toggle" onClick={onEdit}>
              Редактировать
            </Button>
            <span className="venue-edit__audit-hint">{AUDIT_HINT}</span>
          </div>
        ) : null}

        <dl className="venue-sheet" data-testid="venue-sheet-list">
          {rows.map((row) => (
            <div className="venue-sheet__row" key={row.testId} data-testid={row.testId}>
              <dt className="venue-sheet__label">{row.label}</dt>
              <dd className="venue-sheet__value">
                <span className={row.value === null || row.value === undefined || row.value === '' ? 'cell-muted' : undefined}>
                  {sheetCellLabel(row.value)}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/**
 * One url / course / place / format form, used for both the add and the inline
 * edit. Validation and the diff live in `venue-links-model` (unit-tested); the
 * 409 the api answers for a url or a slot another row already holds is printed
 * under the fields.
 */
function LinkForm({
  testIdPrefix,
  form: initial,
  pending,
  conflict,
  submitLabel = 'Сохранить',
  onCancel,
  onSubmit,
}: {
  testIdPrefix: string;
  form: ReturnType<typeof linkFormFrom>;
  pending: boolean;
  conflict: string | null;
  submitLabel?: string;
  onCancel?: () => void;
  onSubmit: (form: ReturnType<typeof linkFormFrom>) => void;
}) {
  const [form, setForm] = useState(initial);
  const set = (key: keyof ReturnType<typeof linkFormFrom>) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }) as ReturnType<typeof linkFormFrom>);

  const ready = linkCreateFrom(form) !== null;
  const untouched = !linkFormIsDirty(initial, form);

  return (
    <form
      className="inline-form"
      data-testid={`${testIdPrefix}-form`}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !untouched) onSubmit(form);
      }}
    >
      <Field label="Ссылка" htmlFor={`${testIdPrefix}-url`} error={linkFieldError(form, 'url')}>
        <Input
          id={`${testIdPrefix}-url`}
          data-testid={`${testIdPrefix}-url`}
          type="url"
          placeholder="https://www.myskills.gov.ru/pgu/…"
          value={form.url}
          onChange={(event) => set('url')(event.target.value)}
        />
      </Field>
      <Field label="Курс" htmlFor={`${testIdPrefix}-course`} error={linkFieldError(form, 'courseId')}>
        <Input
          id={`${testIdPrefix}-course`}
          data-testid={`${testIdPrefix}-course`}
          inputMode="numeric"
          className="input--narrow"
          value={form.courseId}
          onChange={(event) => set('courseId')(event.target.value)}
        />
      </Field>
      <Field label="Позиция" htmlFor={`${testIdPrefix}-place`} error={linkFieldError(form, 'placeId')}>
        <Input
          id={`${testIdPrefix}-place`}
          data-testid={`${testIdPrefix}-place`}
          inputMode="numeric"
          className="input--narrow"
          value={form.placeId}
          onChange={(event) => set('placeId')(event.target.value)}
        />
      </Field>
      <Field label="Формат" htmlFor={`${testIdPrefix}-format`}>
        <Select
          id={`${testIdPrefix}-format`}
          data-testid={`${testIdPrefix}-format`}
          value={form.format}
          onChange={(event) => set('format')(event.target.value)}
        >
          {LINK_FORMAT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>
      {conflict ? (
        <p className="field__error" role="alert" data-testid={`${testIdPrefix}-error`}>
          {conflict}
        </p>
      ) : null}
      <div className="inline-form__actions">
        <Button
          variant="primary"
          size="sm"
          loading={pending}
          disabled={!ready || untouched}
          data-testid={`${testIdPrefix}-submit`}
          onClick={() => {
            if (ready && !untouched) onSubmit(form);
          }}
        >
          {submitLabel}
        </Button>
        {onCancel ? (
          <Button size="sm" data-testid={`${testIdPrefix}-cancel`} onClick={onCancel}>
            Отмена
          </Button>
        ) : null}
      </div>
    </form>
  );
}
